import { useState, useEffect, useCallback, useMemo } from 'react';
import { Form, Button } from 'react-bootstrap';
import { PlusLg, GeoAlt, Calendar, ChevronUp, ChevronDown, Camera, CheckCircleFill, Trash } from 'react-bootstrap-icons';
import axios from 'axios';
import { AdminCard } from '../Admin/shared/AdminCard';
import { PostLeadModal } from '../Marketplace/PostLeadModal';

export function ReferrerLeads({ idToken }) {
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showPostModal, setShowPostModal] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [uploading, setUploading] = useState(null);
    const [uploadError, setUploadError] = useState(null);

    const handleDelete = async (lead) => {
        if (!window.confirm('Delete this lead? This cannot be undone.')) return;
        setUploadError(null);
        try {
            await axios.delete(`${process.env.REACT_APP_CORE_API}/lead/${lead.id}`, { headers: { Authorization: idToken } });
            setLeads(prev => prev.filter(l => l.id !== lead.id));
        } catch (err) {
            setUploadError(err.response?.data?.detail || err.response?.data || 'Failed to delete lead');
        }
    };

    const handlePhoto = async (lead, file) => {
        if (!file) return;
        setUploading(lead.id);
        setUploadError(null);
        const data = new FormData();
        data.append('photo', file);
        try {
            const res = await axios.post(
                `${process.env.REACT_APP_CORE_API}/lead/${lead.id}/photo`,
                data,
                { headers: { Authorization: idToken } }
            );
            setLeads(prev => prev.map(l => (l.id === lead.id ? res.data : l)));
        } catch (err) {
            setUploadError(err.response?.data?.detail || err.response?.data || 'Upload failed');
        } finally {
            setUploading(null);
        }
    };
    const [statusFilter, setStatusFilter] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });

    const fetchLeads = useCallback(async () => {
        if (!idToken) return;

        try {
            setLoading(true);
            const response = await axios.get(
                `${process.env.REACT_APP_CORE_API}/lead`,
                { headers: { Authorization: idToken } }
            );
            setLeads(response.data.value || response.data || []);
        } catch (error) {
            console.error('Failed to fetch leads:', error);
        } finally {
            setLoading(false);
        }
    }, [idToken]);

    useEffect(() => {
        fetchLeads();
    }, [fetchLeads]);

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const SortableHeader = ({ label, sortKey }) => (
        <th onClick={() => handleSort(sortKey)} style={{ cursor: 'pointer', userSelect: 'none' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                {label}
                {sortConfig.key === sortKey && (
                    sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                )}
            </span>
        </th>
    );

    const filteredLeads = useMemo(() => {
        let result = leads.filter(lead => {
            if (statusFilter && lead.status !== statusFilter) return false;
            if (!searchTerm) return true;

            const searchLower = searchTerm.toLowerCase();
            return (
                lead.firstName?.toLowerCase().includes(searchLower) ||
                lead.lastName?.toLowerCase().includes(searchLower) ||
                lead.state?.toLowerCase().includes(searchLower)
            );
        });

        if (sortConfig.key) {
            result = [...result].sort((a, b) => {
                let aVal, bVal;

                if (sortConfig.key === 'name') {
                    aVal = `${a.firstName || ''} ${a.lastName || ''}`.toLowerCase();
                    bVal = `${b.firstName || ''} ${b.lastName || ''}`.toLowerCase();
                } else if (sortConfig.key === 'createdAt') {
                    aVal = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                    bVal = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                } else if (sortConfig.key === 'price') {
                    aVal = a.price || 0;
                    bVal = b.price || 0;
                } else {
                    aVal = (a[sortConfig.key] || '').toString().toLowerCase();
                    bVal = (b[sortConfig.key] || '').toString().toLowerCase();
                }

                if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }

        return result;
    }, [leads, searchTerm, statusFilter, sortConfig]);

    const currencyFormatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    });

    const getStatusBadge = (status) => {
        const styles = {
            ForSale: { bg: 'var(--qmi-warning-light)', color: 'var(--qmi-warning)', label: 'For Sale' },
            Sold: { bg: 'var(--qmi-success-light)', color: 'var(--qmi-success)', label: 'Sold' },
            Closed: { bg: 'var(--qmi-gray-100)', color: 'var(--qmi-gray-500)', label: 'Closed' },
            Inquiry: { bg: 'var(--qmi-primary-light)', color: 'var(--qmi-primary)', label: 'Inquiry' }
        };
        const style = styles[status] || styles.ForSale;
        return (
            <span style={{
                padding: '0.25rem 0.75rem',
                borderRadius: '1rem',
                fontSize: '0.75rem',
                fontWeight: 500,
                backgroundColor: style.bg,
                color: style.color
            }}>
                {style.label}
            </span>
        );
    };

    if (loading) {
        return (
            <div className="admin-loading">
                <div className="admin-loading-spinner" />
            </div>
        );
    }

    return (
        <div>
            <div className="admin-page-header">
                <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
                    <div>
                        <h1 className="admin-page-title">My Leads</h1>
                        <p className="admin-page-description">
                            View and manage all your submitted leads
                        </p>
                    </div>
                    <Button className="qmi-btn-primary" onClick={() => setShowPostModal(true)}>
                        <PlusLg size={16} className="me-2" />
                        Submit New Lead
                    </Button>
                </div>
            </div>

            <AdminCard noPadding>
                {/* Filters */}
                <div style={{ padding: 'var(--space-4)', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                    <Form.Control
                        type="text"
                        placeholder="Search leads..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{ maxWidth: '300px' }}
                    />
                    <Form.Select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        style={{ maxWidth: '150px' }}
                    >
                        <option value="">All Status</option>
                        <option value="ForSale">For Sale</option>
                        <option value="Sold">Sold</option>
                        <option value="Closed">Closed</option>
                    </Form.Select>
                </div>

                {uploadError && (
                    <div className="alert alert-danger mx-3 mb-0" role="alert">{uploadError}</div>
                )}
                {/* Desktop Table */}
                <div className="admin-table-wrapper admin-table-desktop">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <SortableHeader label="Contact" sortKey="name" />
                                <SortableHeader label="State" sortKey="state" />
                                <SortableHeader label="Price" sortKey="price" />
                                <SortableHeader label="Status" sortKey="status" />
                                <SortableHeader label="Date" sortKey="createdAt" />
                                <th>Photo</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredLeads.length === 0 ? (
                                <tr>
                                    <td colSpan={7}>
                                        <div className="admin-empty-state">
                                            <p className="admin-empty-description">No leads found</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                filteredLeads.map(lead => (
                                    <tr key={lead.id}>
                                        <td>
                                            <strong>{lead.firstName} {lead.lastName?.charAt(0)}.</strong>
                                        </td>
                                        <td>
                                            <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                <GeoAlt size={14} style={{ color: 'var(--qmi-gray-400)' }} />
                                                {lead.state}
                                            </span>
                                        </td>
                                        <td>{currencyFormatter.format(lead.price || 0)}</td>
                                        <td>{getStatusBadge(lead.status)}</td>
                                        <td>
                                            <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                <Calendar size={14} style={{ color: 'var(--qmi-gray-400)' }} />
                                                {lead.createdAt ? new Date(lead.createdAt).toLocaleDateString() : 'N/A'}
                                            </span>
                                        </td>
                                        <td>
                                            <label className={`btn btn-sm ${lead.photoUrl ? 'btn-outline-success' : 'btn-outline-primary'} mb-0`} style={{ cursor: 'pointer' }}>
                                                {uploading === lead.id ? (
                                                    'Uploading...'
                                                ) : lead.photoUrl ? (
                                                    <><CheckCircleFill size={14} className="me-1" />Replace</>
                                                ) : (
                                                    <><Camera size={14} className="me-1" />Add Photo</>
                                                )}
                                                <input
                                                    type="file"
                                                    accept="image/jpeg,image/png,image/webp"
                                                    hidden
                                                    disabled={uploading === lead.id}
                                                    onChange={(e) => { handlePhoto(lead, e.target.files[0]); e.target.value = ''; }}
                                                />
                                            </label>
                                        </td>
                                        <td>
                                            {lead.status !== 'Sold' && (
                                                <Button variant="link" className="text-danger p-0" title="Delete lead" onClick={() => handleDelete(lead)}>
                                                    <Trash size={16} />
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Mobile Cards */}
                <div className="admin-mobile-cards" style={{ padding: 'var(--space-4)' }}>
                    {filteredLeads.length === 0 ? (
                        <div className="admin-empty-state">
                            <p className="admin-empty-description">No leads found</p>
                        </div>
                    ) : (
                        filteredLeads.map(lead => (
                            <div key={lead.id} className="admin-mobile-card">
                                <div className="admin-mobile-card-header">
                                    <span className="admin-mobile-card-title">
                                        {lead.firstName} {lead.lastName?.charAt(0)}.
                                    </span>
                                    {getStatusBadge(lead.status)}
                                </div>
                                <div className="admin-mobile-card-row">
                                    <span className="admin-mobile-card-label">State</span>
                                    <span className="admin-mobile-card-value">{lead.state}</span>
                                </div>
                                <div className="admin-mobile-card-row">
                                    <span className="admin-mobile-card-label">Price</span>
                                    <span className="admin-mobile-card-value">{currencyFormatter.format(lead.price || 0)}</span>
                                </div>
                                <div className="admin-mobile-card-row">
                                    <span className="admin-mobile-card-label">Date</span>
                                    <span className="admin-mobile-card-value">
                                        {lead.createdAt ? new Date(lead.createdAt).toLocaleDateString() : 'N/A'}
                                    </span>
                                </div>
                                {lead.status !== 'Sold' && (
                                    <Button variant="outline-danger" size="sm" className="mt-2 w-100" onClick={() => handleDelete(lead)}>
                                        <Trash size={14} className="me-1" />Delete
                                    </Button>
                                )}
                            </div>
                        ))
                    )}
                </div>
            </AdminCard>

            <PostLeadModal
                show={showPostModal}
                onHide={() => setShowPostModal(false)}
                idToken={idToken}
                onLeadPosted={fetchLeads}
            />
        </div>
    );
}
